import {Injectable} from '@angular/core';
import {IDBPDatabase, openDB} from 'idb';
import {
  dbAuthStore,
  dbBusinessConfig,
  dbBusinessInfo,
  dbClientsStore,
  dbConektaClientsStore,
  dbConektaReferencesStore,
  dbContractsStore,
  dbDynamicReferencesStore,
  dbIndexAuth,
  dbIndexBusiness,
  dbIndexConektaClientsRef,
  dbIndexNotification,
  dbIndexPassportClientsRef,
  dbIndexPayments,
  dbIndexUsers,
  dbName,
  dbNotificationsStore,
  dbPassportReferencesStore,
  dbPaymentsStore,
  dbUsersStore,
  dbVersion,
  keyPathCurp,
  keyPathId,
  keyPathReference,
  keyUserID
} from "../utils/config";
import {
  eProvider,
  eRoles,
  oBusinessConfig,
  oBusinessInfo,
  oClient,
  oContract,
  oNotification,
  oUser,
  tyClientContract
} from "ravcredit-core";

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  indexDbStatus = false
  iDb!: IDBPDatabase<unknown>

  constructor() {
    if (('indexedDB' in window)) {
      this.indexDbStatus = true
      this.initDB().then()
    } else {
    }
  }

  async initDB() {
    this.iDb = await openDB(dbName, dbVersion, {
      upgrade(db: IDBPDatabase) {

        if (!db.objectStoreNames.contains(dbAuthStore)) {
          db.createObjectStore(
            dbAuthStore,
            {keyPath: "id", autoIncrement: true}
          ).createIndex(dbIndexAuth, dbIndexAuth, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbUsersStore)) {
          db.createObjectStore(
            dbUsersStore,
            {keyPath: "id", autoIncrement: true}
          ).createIndex(dbIndexUsers, dbIndexUsers, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbContractsStore)) {
          const storeContract = db.createObjectStore(
            dbContractsStore,
            {keyPath: keyPathId, autoIncrement: true}
          )
          storeContract.createIndex(keyPathId, keyPathId, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbClientsStore)) {
          const storeClient = db.createObjectStore(
            dbClientsStore,
            {keyPath: [keyPathId, keyPathCurp], autoIncrement: false}
          )
          storeClient.createIndex(keyPathId, keyPathId, {unique: true})
          storeClient.createIndex(keyPathCurp, keyPathCurp, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbConektaClientsStore)) {
          const storeConekta = db.createObjectStore(
            dbConektaClientsStore,
            {keyPath: [keyPathId, keyPathCurp], autoIncrement: false}
          )
          storeConekta.createIndex(keyPathId, keyPathId, {unique: true})
          storeConekta.createIndex(keyPathCurp, keyPathCurp, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbConektaReferencesStore)) {
          db.createObjectStore(
            dbConektaReferencesStore,
            {keyPath: "id", autoIncrement: true}
          ).createIndex(dbIndexConektaClientsRef, dbIndexConektaClientsRef, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbDynamicReferencesStore)) {
          const storeConekta = db.createObjectStore(
            dbDynamicReferencesStore,
            {keyPath: [keyPathId, keyPathReference], autoIncrement: false}
          )
          storeConekta.createIndex(keyPathId, keyPathId, {unique: true})
          storeConekta.createIndex(keyPathReference, keyPathReference, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbPassportReferencesStore)) {
          db.createObjectStore(
            dbPassportReferencesStore,
            {keyPath: "id", autoIncrement: true}
          ).createIndex(dbIndexPassportClientsRef, dbIndexPassportClientsRef, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbBusinessInfo)) {
          db.createObjectStore(
            dbBusinessInfo,
            {keyPath: "version"}
          ).createIndex(dbIndexBusiness, dbIndexBusiness, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbBusinessConfig)) {
          db.createObjectStore(
            dbBusinessConfig,
            {keyPath: "version"}
          ).createIndex(dbIndexBusiness, dbIndexBusiness, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbNotificationsStore)) {
          db.createObjectStore(
            dbNotificationsStore,
            {keyPath: "notification_id"}
          ).createIndex(dbIndexNotification, dbIndexNotification, {unique: true})
        }

        if (!db.objectStoreNames.contains(dbPaymentsStore)) {
          db.createObjectStore(
            dbPaymentsStore,
            {keyPath: "id"}
          ).createIndex(dbIndexPayments, dbIndexPayments, {unique: true})
        }
      }
    })
  }

  async getAllStoreObject(store: string) {
    const db = await openDB(dbName, dbVersion)
    return await db.getAll(store)
  }

  /******************** USERS *************************************/

  async addUsers(store: string, data: any[]) {
    for (const o of data) {
      const exists = await this.dbGetObject(store, o["id"])
      if (!exists) await this.dbAddObject(store, o)
    }
  }

  async updateUserData(store: string, data: any) {
    const db = await openDB(dbName, dbVersion)
    await db.delete(store, data.id)
    await this.dbAddObject(store, data)
    db.close()
    return this.getAllStoreObject(dbUsersStore)
  }

  /************************************************** GENERAL *********************************************************/
  async dbAddObject<T>(store: string, data: any): Promise<boolean> {
    try {
      const db = await openDB(dbName, dbVersion)
      await db.put(store, data)
      return true
    } catch (e) {
      return false
    }
  }

  async dbUpdateObject<T>(store: string, data: any) {
    const db = await openDB(dbName, dbVersion)
    await db.put(store, data)
  }

  async dbAddBuildByStore(store: string, data: any[]) {
    for (const o of data) {
      const exists = await this.dbGetObject(store, o["id"])
      if (!exists) await this.dbAddObject(store, o)
      else await this.dbUpdateObject(store, o)
    }
  }

  async dbGetObject<T>(store: string, data: any): Promise<T> {
    if (!data) return data
    const db = await openDB(dbName, dbVersion)
    return await db.get(store, data) as T
  }

  async dbGetObjectByID<T>(store: string, id: string) {
    try {
      const db = await openDB(dbName, dbVersion)
      const txn = db.transaction(store, "readwrite")
      const obj = txn.objectStore(store)
      const index = obj.index(keyPathId)
      const va2 = await index.get(id)
      return va2
    } catch (e) {
      console.error(e)
      return {} as T
    }
  }

  async dbDeleteObjectByID<T>(store: string, id: string) {
    try {
      const db = await openDB(dbName, dbVersion)
      const txn = db.transaction(store, "readwrite")
      const obj = txn.objectStore(store)
      const index = obj.index(keyPathId)
      const va2 = index.openCursor(id)
      va2.then(async cursor => {
        while (cursor) {
          cursor.delete();
          cursor = await cursor.continue();
        }
      })
      return va2
    } catch (e) {
      console.error(e)
      return {} as T
    }
  }

  async dbGetObjectCurp<T>(store: string, data: any): Promise<T> {
    try {
      const db = await openDB(dbName, dbVersion)
      const txn = db.transaction(store, "readwrite")
      const obj = txn.objectStore(store)
      const index = obj.index(keyPathCurp)
      const va2 = await index.get(data)
      return va2
    } catch (e) {
      console.error(e)
      return {} as T
    }
  }

  async dbRemoveObject<T>(store: string, data: any): Promise<T> {
    const db = await openDB(dbName, dbVersion)
    return await db.delete(store, data) as T
  }

  async cleanAll<T>(store: string) {
    const db = await openDB(dbName, dbVersion)
    return await db.clear(store)
  }

  async dbObjectCount(store: string) {
    const db = await openDB(dbName, dbVersion)
    return await db.count(store)
  }

  async upsertObjectById<T>(store: string, id: string, data: T) {

    const db = await openDB(dbName, dbVersion)
    const res = await db.get(store, id)

    if (res) await this.dbRemoveObject<T>(store, id)

    return await db.add(store, data)
  }

  async dbGetAllByStore<T>(store: string, index?: string) {
    const db = await openDB(dbName, dbVersion)
    return await db.getAll(store) as T
  }

  async updateObjectByID<T>(store: string, id: string, data: T): Promise<boolean> {
    try {

      const db = await openDB(dbName, dbVersion)
      const res = await db.get(store, id) as oClient

      if (res != undefined) await this.dbRemoveObject(dbClientsStore, id)

      return await this.dbAddObject(store, data)

    } catch (e) {
      console.error("updateObjectByID Catch Error:", e)
      return false
    }
  }

  async dbPatchObject<T>(store: string, data: any): Promise<T> {
    try {
      const db = await openDB(dbName, dbVersion)
      const txn = db.transaction(store, "readwrite")
      const obj = txn.objectStore(store)
      // const index = obj.index(keyPathCurp)
      await obj.put(data)
      return data as T
    } catch (e) {
      console.error(e)
      return {} as T
    }
  }

  /*********************** CLIENTS ****************************************/
  async updateClientStatus(_client: Partial<oClient>, id: string) {
    const db = await openDB(dbName, dbVersion)
    const client = await db.get(dbClientsStore, id)
    const c = client as oClient
    if (c == null || c.status == undefined) return

    c.status = _client.status as string
    await this.upsertObjectById(dbClientsStore, id, c)
  }

  /*********************** Payments ****************************************/
  async getPaymentsByContract<T>(id: string): Promise<T> {
    const db = await openDB(dbName, dbVersion)
    return await db.get(dbPaymentsStore, id) as T
  }

  /********************** CONTRACTS ***************************************/

  async dbGetAllContracts() {
    const db = await openDB(dbName, dbVersion)
    return await db.getAll(dbContractsStore) as oContract[]
  }

  async updateContractStatus(_contract: Partial<oContract>, id: string) {
    const db = await openDB(dbName, dbVersion)
    const contract = await db.get(dbContractsStore, id)
    const c = contract as oContract
    if (c == null || c.active == undefined) return

    c.active = _contract.active as boolean
    await this.upsertObjectById(dbContractsStore, id, c)
  }

  /******************** MERGE **************************************/

  async dbGetAllClientContracts(): Promise<tyClientContract[]> {
    const clientContract: tyClientContract[] = []
    const clients = await this.dbGetAllByStore<oClient[]>(dbClientsStore)
    if (clients.length == 0) return []

    for (let client of clients) {
      const contract = await this.dbGetObject<oContract>(dbContractsStore, client.contract)
      const e = {...client, ...contract}
      // preserve the id value over concat models
      e.id = client.id
      e.status = contract.status
      clientContract.push(e)
    }
    return clientContract
  }

  /****************** BUSINESS INFO *************************************/

  async getBusinessInfo(): Promise<oBusinessInfo | null> {
    const config: oBusinessInfo[] = await this.dbGetAllByStore<oBusinessInfo[]>(dbBusinessInfo)

    if (!config) return null

    return config[0]
  }

  /****************** BUSINESS CONFIG ************************************/

  async getBusinessConfig(): Promise<oBusinessConfig | null> {
    const config: oBusinessConfig[] = await this.dbGetAllByStore<oBusinessConfig[]>(dbBusinessConfig)

    if (!config) return null

    return config[0]
  }

  async updateBusinessConfig(config: oBusinessConfig) {

    // config.version = "1.0"
    await this.dbRemoveObject(dbBusinessConfig, "1.0")
    await this.dbAddObject(dbBusinessConfig, config)
  }

  async updateBusinessInfo(config: oBusinessInfo) {

    config.version = "v2024"
    await this.dbRemoveObject(dbBusinessInfo, "v2024")
    await this.dbAddObject(dbBusinessInfo, config)
  }

  /**************** NOTIFICATIONS *********************************/

  async getLocalNotifications(): Promise<oNotification[] | null> {
    const notifications: oNotification[] = await this.dbGetAllByStore<oNotification[]>(dbNotificationsStore)
    if (!notifications) return null
    return notifications
  }

  async updateNotifications(notifications: oNotification[]) {
    for (const o of notifications) {
      const exists = await this.dbGetObject(dbNotificationsStore, o.notification_id)
      if (!exists) {
        await this.dbAddObject(dbNotificationsStore, o)
      }
    }
  }

  /******************** AUTH **************************************/

  async dbSignIn(store: string, data: any) {
    await this.iDb.put(store, data)
    const _data = data as oUser
    this.setLocalStorage(keyUserID, _data.id)
  }

  async dbSignOut() {

    const k = this.getLocalUser()
    if (!k) return

    await this.iDb.delete(dbAuthStore, k)
    this.removeLocalStorage(keyUserID)
  }

  async dbAuthorization() {
    const k = this.getLocalUser()
    if (k == null) return false

    const db = await openDB(dbName, dbVersion)
    // utils store = db.transaction(dbUsersStore).objectStore(dbUsersStore)
    const auth = await db.get(dbAuthStore, k)
    // utils auth = await store.get(k)
    db.close()

    if (!auth) return false
    else return true
  }

  async dbGetToken() {
    const k = this.getLocalUser()
    if (k == null) return ""

    const db = await openDB(dbName, dbVersion)
    // utils store = db.transaction(dbUsersStore).objectStore(dbUsersStore)
    const auth = await db.get(dbAuthStore, k) as oUser
    // utils auth = await store.get(k)

    db.close()
    return auth.access_token != "" ? auth.access_token : ""
  }

  async dbGetRole() {
    const k = this.getLocalUser()
    if (k == null) return ""

    const db = await openDB(dbName, dbVersion)
    const auth = await db.get(dbAuthStore, k) as oUser

    db.close()
    return auth.role ? auth.role : eRoles.Consultant
  }

  async dbGetPlatformUser() {
    const k = this.getLocalUser()
    if (k == null) return ""

    const db = await openDB(dbName, dbVersion)
    // utils store = db.transaction(dbUsersStore).objectStore(dbUsersStore)
    const auth = await db.get(dbAuthStore, k) as oUser
    // utils auth = await store.get(k)

    db.close()
    return auth.name != "" ? auth.name : ""
  }

  /****************** LOCAL STORAGE ************************************/

  getLocalUser() {
    const k = localStorage.getItem(keyUserID)
    return (k)
  }

  async getAuthUser() {
    const data = await this.dbGetAllByStore<oUser[]>(dbAuthStore)
    return data[0]
  }

  async getAuthName() {
    const data = await this.getAuthUser()
    return data.name
  }

  async getAuthUsername() {
    const data = await this.getAuthUser()
    return data.username
  }

  setLocalStorage(k: string, v: string) {
    localStorage.setItem(k, v)
  }

  removeLocalStorage(k: string) {
    localStorage.removeItem(k)
  }

  getLocalStorage(k: string): string | null {
    return localStorage.getItem(k)
  }

  async saveDataLocally<T>(
    contract: oContract,
    client: oClient,
    providerData: T,
  ): Promise<boolean> {
    try {
      await this.dbAddObject(dbClientsStore, client)
      await this.dbAddObject(dbContractsStore, contract)

      if (!providerData) return false

      if (contract.financial.provider === eProvider.Conekta) {
        await this.dbAddObject(dbConektaClientsStore, providerData)
        await this.dbAddObject(dbConektaReferencesStore, providerData)
      } else if (contract.financial.provider === eProvider.Passport) {
        await this.dbAddObject(dbPassportReferencesStore, providerData)
      } else if (contract.financial.provider === eProvider.DynamiCore) {
        await this.dbAddObject(dbDynamicReferencesStore, providerData)
      }
      return true
    } catch (e) {
      console.error(e)
      return false
    }
  }
}
